import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Grid, Skeleton, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import api from '../../config/api';

// Bucket config — declarative so the layout is uniform. label/description show
// in the card header; severity tints the count chip; linkTo is the drill page.
const BUCKETS = [
  {
    key: 'pos_awaiting_approval',
    label: 'POs Awaiting Approval',
    description: 'Submitted POs sitting in the approval queue',
    severity: 'info',
    linkTo: '/procurement/purchase-orders?status=submitted',
    rowLink: (r) => `/procurement/purchase-orders/${r.id}`,
    columns: [
      { key: 'po_number', label: 'PO #' },
      { key: 'supplier_id', label: 'Supplier ID' },
      { key: 'po_date', label: 'Date', format: (v) => v ? String(v).slice(0, 10) : '—' },
      { key: 'total_amount', label: 'Total', align: 'right', format: (v, r) => `${r.currency || ''} ${Number(v || 0).toFixed(2)}` },
    ],
  },
  {
    key: 'pos_overdue_no_gr',
    label: 'POs Overdue — No Receipt',
    description: 'Past expected_delivery_date with no goods receipt yet',
    severity: 'error',
    linkTo: '/procurement/purchase-orders?status=sent',
    rowLink: (r) => `/procurement/purchase-orders/${r.id}`,
    columns: [
      { key: 'po_number', label: 'PO #' },
      { key: 'supplier_id', label: 'Supplier ID' },
      { key: 'expected_delivery_date', label: 'Expected', format: (v) => v ? String(v).slice(0, 10) : '—' },
      { key: 'days_overdue', label: 'Days Late', align: 'right', format: (v) => `${v}d` },
      { key: 'total_amount', label: 'Total', align: 'right', format: (v, r) => `${r.currency || ''} ${Number(v || 0).toFixed(2)}` },
    ],
  },
  {
    key: 'transfers_past_eta',
    label: 'Transfers Past ETA',
    description: 'In-transit transfers that should have arrived',
    severity: 'warning',
    linkTo: '/inventory/transfers?status=in_transit',
    rowLink: (r) => `/inventory/transfers/${r.id}`,
    columns: [
      { key: 'transfer_number', label: 'Transfer #' },
      { key: 'from_warehouse_code', label: 'From' },
      { key: 'to_warehouse_code', label: 'To' },
      { key: 'expected_arrival_date', label: 'Expected', format: (v) => v ? String(v).slice(0, 10) : '—' },
      { key: 'days_overdue', label: 'Days Late', align: 'right', format: (v) => `${v}d` },
    ],
  },
  {
    key: 'wholesale_pending_ship_over_24h',
    label: 'Pending Ship > 24h',
    description: 'Wholesale orders waiting to ship for more than a day',
    severity: 'warning',
    linkTo: '/wholesale/pending-ship',
    rowLink: (r) => `/wholesale/new?edit=${r.order_id}&brand=${r.brand_id}`,
    columns: [
      { key: 'order_id', label: 'Order #', format: (v) => String(v || '').replace(/^CIN7-/, '') },
      { key: 'brand_name', label: 'Brand' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'event_date', label: 'Date', format: (v) => v ? String(v).slice(0, 10) : '—' },
      { key: 'age_hours', label: 'Age', align: 'right', format: (v) => v >= 24 ? `${Math.round(v/24)}d` : `${v}h` },
    ],
  },
  {
    key: 'wholesale_ready_to_invoice_over_48h',
    label: 'Awaiting Invoice > 48h',
    description: 'Shipped but not yet invoiced for more than 2 days',
    severity: 'warning',
    linkTo: '/wholesale/ready-to-invoice',
    rowLink: (r) => `/wholesale/ready-to-invoice`,
    columns: [
      { key: 'order_id', label: 'Order #', format: (v) => String(v || '').replace(/^CIN7-/, '') },
      { key: 'brand_name', label: 'Brand' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'shipped_date', label: 'Shipped', format: (v) => v ? String(v).slice(0, 10) : '—' },
      { key: 'age_hours', label: 'Age', align: 'right', format: (v) => `${Math.round(v/24)}d` },
    ],
  },
  {
    key: 'low_stock_skus',
    label: 'Out of Stock SKUs',
    description: 'Sellable SKUs with zero or negative available inventory',
    severity: 'error',
    linkTo: '/inventory/stock?filter=out_of_stock',
    rowLink: (r) => `/inventory/stock?search=${encodeURIComponent(r.sku)}`,
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'product_name', label: 'Product' },
      { key: 'available', label: 'Available', align: 'right', format: (v) => Number(v || 0).toLocaleString() },
    ],
  },
];

const SEVERITY_COLOR = { info: 'info', warning: 'warning', error: 'error', default: 'default' };

export default function ActionQueue() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/operations/action-queue');
      setData(r.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load action queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalActions = data ? BUCKETS.reduce((sum, b) => sum + (data[b.key]?.count || 0), 0) : 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Action Queue</Typography>
          <Typography variant="caption" color="text.secondary">
            Everything that needs your attention today {data?.generated_at && `· refreshed ${new Date(data.generated_at).toLocaleTimeString()}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!loading && (
            <Chip
              label={`${totalActions} item${totalActions === 1 ? '' : 's'}`}
              color={totalActions === 0 ? 'success' : 'primary'}
              sx={{ fontWeight: 700 }}
            />
          )}
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && totalActions === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Everything's clear — no overdue POs, transfers, shipments, or stock-outs right now.
        </Alert>
      )}

      <Grid container spacing={2}>
        {BUCKETS.map(bucket => {
          const block = data?.[bucket.key] || { count: 0, samples: [] };
          const isEmpty = !loading && block.count === 0;
          return (
            <Grid item xs={12} md={6} key={bucket.key}>
              <Card sx={{ height: '100%', opacity: isEmpty ? 0.55 : 1 }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{bucket.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{bucket.description}</Typography>
                    </Box>
                    {loading ? (
                      <Skeleton variant="rounded" width={40} height={28} />
                    ) : (
                      <Chip
                        label={block.count}
                        color={block.count > 0 ? SEVERITY_COLOR[bucket.severity] : 'default'}
                        sx={{ fontWeight: 700, minWidth: 48 }}
                        onClick={() => navigate(bucket.linkTo)}
                      />
                    )}
                  </Stack>

                  {loading && (
                    <Box sx={{ pt: 1 }}>
                      <Skeleton height={32} />
                      <Skeleton height={32} />
                      <Skeleton height={32} />
                    </Box>
                  )}

                  {!loading && block.count > 0 && (
                    <TableContainer sx={{ mt: 1 }}>
                      <Table size="small" sx={{ '& .MuiTableCell-head': { fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, py: 0.5 }, '& .MuiTableCell-body': { py: 0.75 } }}>
                        <TableHead>
                          <TableRow>
                            {bucket.columns.map(c => (
                              <TableCell key={c.key} align={c.align || 'left'}>{c.label}</TableCell>
                            ))}
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {block.samples.map((row, i) => (
                            <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                              onClick={() => navigate(bucket.rowLink(row))}>
                              {bucket.columns.map(c => (
                                <TableCell key={c.key} align={c.align || 'left'}
                                  sx={{ fontSize: 12, fontFamily: c.align === 'right' ? 'monospace' : 'inherit' }}>
                                  {c.format ? c.format(row[c.key], row) : (row[c.key] ?? '—')}
                                </TableCell>
                              ))}
                              <TableCell align="right" sx={{ width: 32 }}><ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {!loading && block.count > 5 && (
                    <Box sx={{ mt: 1, textAlign: 'right' }}>
                      <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(bucket.linkTo)}>
                        See all {block.count} →
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
